import { Link } from "react-router-dom";
import { PublicDocLayout, PublicSectionAnchor } from "@/components/PublicDocLayout";
import { PublicLayout } from "@/components/PublicLayout";
import { PublicPageHero } from "@/components/PublicPageHero";
import {
  ATW_CONTACT_URL,
  ATW_MAILING_ADDRESS,
  ATW_TERMS_URL,
  LEGAL_DBA,
  LEGAL_ENTITY,
  LEGAL_NAME_SHORT,
  SUPPORT_EMAIL,
  TERMS_UPDATED,
} from "@/lib/publicSite";

const TERMS_TOC = [
  { id: "eligibility", label: "Eligibility" },
  { id: "account", label: "Your account" },
  { id: "connections", label: "Connected services" },
  { id: "acceptable-use", label: "Acceptable use" },
  { id: "ai", label: "Hub AI" },
  { id: "privacy", label: "Privacy" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "liability", label: "Liability" },
  { id: "governing-law", label: "Governing law" },
  { id: "changes", label: "Changes" },
  { id: "contact", label: "Contact" },
] as const;

export function TermsPage() {
  return (
    <PublicLayout title="ATW Hub Terms of Use">
      <article className="public-doc">
        <PublicPageHero
          eyebrow="Legal"
          title="ATW Hub Terms of Use"
          updated={TERMS_UPDATED}
        />

        <p>
          These Terms of Use (“Terms”) are a contract between you and {LEGAL_ENTITY}, doing business as{" "}
          {LEGAL_DBA} (“{LEGAL_NAME_SHORT},” “we,” or “us”), for your use of ATW Hub at hub.atw.net, the
          Hub API at api.hub.atw.net, and related apps (including the ATW Hub app for Slack and Zoom
          integrations) (together, “Hub”). By creating or using a Hub account, connecting a service to
          Hub, or authorizing a Hub integration, you agree to these Terms on behalf of yourself and the
          organization you represent.
        </p>
        <p>
          {LEGAL_NAME_SHORT}'s general website and non-Hub products are covered by the{" "}
          <a href={ATW_TERMS_URL}>ATW Terms and Conditions</a>. If these Terms conflict with those
          terms about Hub, these Terms control for Hub.
        </p>

        <PublicDocLayout toc={[...TERMS_TOC]}>
          <section id="eligibility" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="eligibility" />
              1. Eligibility
            </h2>
            <p>
              Hub is a business tool. You must be at least 16 years old and have authority to bind your
              organization. Your organization's {LEGAL_NAME_SHORT} administrator must enable Hub for you
              before you can use it.
            </p>
          </section>

          <section id="account" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="account" />
              2. Your account
            </h2>
            <ul className="public-list">
              <li>
                You sign in to Hub with your {LEGAL_NAME_SHORT} account at auth.atw.net. Keep your
                credentials secure and tell us promptly at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if you suspect unauthorized
                access.
              </li>
              <li>You are responsible for activity under your Hub account.</li>
              <li>
                We may suspend or terminate access if you violate these Terms, if your organization ends
                Hub service, or if we must protect Hub, our customers, or the law.
              </li>
            </ul>
          </section>

          <section id="connections" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="connections" />
              3. Connected services
            </h2>
            <p>
              Hub can connect to services you choose (for example Slack, Microsoft Teams, Zoom Team Chat
              and Meetings, Google mail and calendar, Zoho, and GIPHY). Each service has its own terms and
              privacy policy. When you connect a service, you authorize Hub to access it on your behalf
              within the permissions you approve.
            </p>
            <p>
              You must have the right to connect each service and the data in it. You may disconnect a
              service in Hub at any time; Hub deletes the tokens and related Hub data as described in the{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section id="acceptable-use" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="acceptable-use" />
              4. Acceptable use
            </h2>
            <p>You agree not to:</p>
            <ul className="public-list">
              <li>use Hub for unlawful purposes or to violate others' rights;</li>
              <li>attempt to access Hub, connected services, or other users' data without authorization;</li>
              <li>interfere with Hub's operation or security, or probe or scan systems without permission;</li>
              <li>reverse engineer Hub except where the law allows;</li>
              <li>use Hub to build or train competing models using others' content without permission.</li>
            </ul>
          </section>

          <section id="ai" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="ai" />
              5. Hub AI
            </h2>
            <p>
              Hub AI uses third-party AI models and Slack's MCP server when you ask questions. AI output
              can be wrong or incomplete. You are responsible for reviewing drafts before you send them and
              for how you use AI answers. Hub AI does not make automated decisions about people.
            </p>
          </section>

          <section id="privacy" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="privacy" />
              6. Privacy
            </h2>
            <p>
              Our <Link to="/privacy">Privacy Policy</Link> explains what Hub collects, how we use it, and
              your choices. By using Hub, you agree to that policy.
            </p>
          </section>

          <section id="disclaimers" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="disclaimers" />
              7. Disclaimers
            </h2>
            <p>
              Hub is provided “as is” and “as available.” We do not guarantee uninterrupted or error-free
              service. Connected services may change, rate-limit, or revoke access outside our control.
            </p>
          </section>

          <section id="liability" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="liability" />
              8. Limitation of liability
            </h2>
            <p>
              To the fullest extent permitted by law, {LEGAL_ENTITY} and its officers, employees, and
              affiliates are not liable for indirect, incidental, special, consequential, or punitive
              damages, or for lost profits, data, or goodwill, arising from Hub or these Terms. Our total
              liability for any claim about Hub is limited to the greater of USD $100 or the amount you paid
              us for Hub in the twelve months before the claim.
            </p>
          </section>

          <section id="governing-law" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="governing-law" />
              9. Governing law and disputes
            </h2>
            <p>
              The laws of the State of Ohio and applicable U.S. federal law govern these Terms, without
              regard to conflict-of-laws rules. Any dispute about Hub or these Terms will be heard only in
              the state courts in Cuyahoga County, Ohio, or the U.S. District Court for the Northern
              District of Ohio, and you and {LEGAL_NAME_SHORT} agree to the jurisdiction of those courts.
              Either party may still ask any court with jurisdiction for urgent relief to protect its data,
              security, or intellectual property.
            </p>
          </section>

          <section id="changes" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="changes" />
              10. Changes
            </h2>
            <p>
              We may update these Terms. We will post the new date at the top and, for material changes,
              give notice in Hub or by email before they take effect. Continued use after the effective date
              means you accept the updated Terms.
            </p>
          </section>

          <section id="contact" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="contact" />
              11. Contact
            </h2>
            <p>
              {LEGAL_ENTITY}, doing business as {LEGAL_DBA}
              <br />
              {ATW_MAILING_ADDRESS}
              <br />
              Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              <br />
              Contact form: <a href={ATW_CONTACT_URL}>atw.net/contact</a>
            </p>
          </section>
        </PublicDocLayout>
      </article>
    </PublicLayout>
  );
}
